import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Alert, Button, CircularProgress, Box, Grid, Typography } from "@mui/material";
import type { Subscription, ServerLicenseDto, CloudSubscriptionDto, SubscriptionGroupDto } from "client/api-v2";
import {
  cloudSubscriptionUserGrantsApi,
  serverLicenseUserGrantsApi,
  subscriptionGroupUserGrantsApi,
} from "client/api/UserGrantApi";
import { DetailsPageLayout } from "components/DetailsPageLayout/DetailsPageLayout";
import { useEntity } from "components/EntityProvider/EntityProvider";
import { UserGrantsView } from "components/UserGrants/UserGrantsView";
import { PolicyAuthorized } from "components/authorization/PolicyAuthorized";
import { RbacPolicies } from "components/authorization/RbacPolicies";
import {
  getServerLicenseBySubscriptionId,
  getCloudSubscriptionBySubscriptionId,
} from "../services/subscriptionService";

/**
 * User Access view for Subscriptions.
 * This component determines the platform type (Server or Cloud) and delegates
 * to the appropriate user grants API with the correct underlying entity ID.
 */
export function SubscriptionUserAccess() {
  const { entity: subscription } = useEntity<Subscription>();
  const location = useLocation();
  const [platformEntityId, setPlatformEntityId] = useState<string | null>(null);
  const [platformEntity, setPlatformEntity] = useState<ServerLicenseDto | CloudSubscriptionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isServerPlatform = subscription?.plan?.productPlatform === "OctopusServer";
  const isCloudPlatform = subscription?.plan?.productPlatform === "OctopusCloud";

  useEffect(() => {
    if (!subscription?.id) {
      setLoading(false);
      setError("Subscription not found");
      return;
    }

    setLoading(true);
    setError(null);

    if (isServerPlatform) {
      getServerLicenseBySubscriptionId(subscription.id)
        .then((license: ServerLicenseDto) => {
          setPlatformEntityId(license.id);
          setPlatformEntity(license);
        })
        .catch((err) => {
          console.error("Failed to fetch server license:", err);
          setError("Failed to load server license information");
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (isCloudPlatform) {
      getCloudSubscriptionBySubscriptionId(subscription.id)
        .then((cloudSub: CloudSubscriptionDto) => {
          setPlatformEntityId(cloudSub.id);
          setPlatformEntity(cloudSub);
        })
        .catch((err) => {
          console.error("Failed to fetch cloud subscription:", err);
          setError("Failed to load cloud subscription information");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
      setError("Unsupported subscription platform type");
    }
  }, [subscription?.id, isServerPlatform, isCloudPlatform]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !platformEntityId || !platformEntity) {
    return <Alert severity="error">{error || "Unable to load user access information"}</Alert>;
  }

  // Get subscription group if available (both Server and Cloud can have subscription groups)
  const group: SubscriptionGroupDto | undefined =
    "subscriptionGroup" in platformEntity ? platformEntity.subscriptionGroup : undefined;

  // Render based on platform type to avoid union type issues with UserGrantsView
  if (isServerPlatform) {
    return (
      <DetailsPageLayout pageName={"User access"}>
        <Grid container spacing={3}>
          <Grid size={6}>
            <Typography variant={"h6"}>Direct user grants</Typography>
          </Grid>
          <Grid
            size={6}
            sx={{
              textAlign: "right",
            }}
          >
            <PolicyAuthorized policies={RbacPolicies.ServerLicenses.ReadWrite}>
              <Button component={Link} to={`${location.pathname}/assign`} color="success" variant="contained">
                Assign user
              </Button>
            </PolicyAuthorized>
          </Grid>
          <Grid size={12}>
            <UserGrantsView
              assetId={platformEntityId}
              userGrantsApi={serverLicenseUserGrantsApi}
              requiredWriteRole={RbacPolicies.ServerLicenses.ReadWrite}
            />
          </Grid>

          <PolicyAuthorized policies={RbacPolicies.SubscriptionGroups.Read}>
            <Grid size={6}>
              <Typography variant={"h6"}>Subscription Group {group && group.name}</Typography>
            </Grid>
            {group && group.id && (
              <>
                <Grid
                  size={6}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  <PolicyAuthorized policies={RbacPolicies.SubscriptionGroups.ReadWrite}>
                    <Button
                      component={Link}
                      to={`${location.pathname}/subscription-group/${group.id}/assign`}
                      color="success"
                      variant="contained"
                    >
                      Assign user
                    </Button>
                  </PolicyAuthorized>
                </Grid>
                <Grid size={12}>
                  <UserGrantsView
                    assetId={group.id}
                    userGrantsApi={subscriptionGroupUserGrantsApi}
                    requiredWriteRole={RbacPolicies.SubscriptionGroups.ReadWrite}
                  />
                </Grid>
              </>
            )}
            {!group && (
              <>
                <PolicyAuthorized policies={RbacPolicies.Admin.ReadWrite}>
                  <Grid
                    size={12}
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    <Alert severity="warning">
                      No subscription group assigned. Choose an existing group or create a new one below.
                    </Alert>
                  </Grid>
                  <Grid
                    size={12}
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    <Button component={Link} to={`${location.pathname}/subscription-group/add-existing`}>
                      Add to existing
                    </Button>
                    <span> or </span>
                    <Button component={Link} to={`${location.pathname}/subscription-group/add-new`}>
                      Create new
                    </Button>
                  </Grid>
                </PolicyAuthorized>
              </>
            )}
          </PolicyAuthorized>
        </Grid>
      </DetailsPageLayout>
    );
  }

  if (isCloudPlatform) {
    return (
      <DetailsPageLayout pageName={"User access"}>
        <Grid container spacing={3}>
          <Grid size={6}>
            <Typography variant={"h6"}>Direct user grants</Typography>
          </Grid>
          <Grid
            size={6}
            sx={{
              textAlign: "right",
            }}
          >
            <PolicyAuthorized policies={RbacPolicies.CloudSubscriptions.ReadWrite}>
              <Button component={Link} to={`${location.pathname}/assign`} color="success" variant="contained">
                Assign user
              </Button>
            </PolicyAuthorized>
          </Grid>
          <Grid size={12}>
            <UserGrantsView
              assetId={platformEntityId}
              userGrantsApi={cloudSubscriptionUserGrantsApi}
              requiredWriteRole={RbacPolicies.CloudSubscriptions.ReadWrite}
            />
          </Grid>

          <PolicyAuthorized policies={RbacPolicies.SubscriptionGroups.Read}>
            <Grid size={6}>
              <Typography variant={"h6"}>Subscription Group {group && group.name}</Typography>
            </Grid>
            {group && group.id && (
              <>
                <Grid
                  size={6}
                  sx={{
                    textAlign: "right",
                  }}
                >
                  <PolicyAuthorized policies={RbacPolicies.SubscriptionGroups.ReadWrite}>
                    <Button
                      component={Link}
                      to={`${location.pathname}/subscription-group/${group.id}/assign`}
                      color="success"
                      variant="contained"
                    >
                      Assign user
                    </Button>
                  </PolicyAuthorized>
                </Grid>
                <Grid size={12}>
                  <UserGrantsView
                    assetId={group.id}
                    userGrantsApi={subscriptionGroupUserGrantsApi}
                    requiredWriteRole={RbacPolicies.SubscriptionGroups.ReadWrite}
                  />
                </Grid>
              </>
            )}
            {!group && (
              <>
                <PolicyAuthorized policies={RbacPolicies.Admin.ReadWrite}>
                  <Grid
                    size={12}
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    <Alert severity="warning">
                      No subscription group assigned. Choose an existing group or create a new one below.
                    </Alert>
                  </Grid>
                  <Grid
                    size={12}
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    <Button component={Link} to={`${location.pathname}/subscription-group/add-existing`}>
                      Add to existing
                    </Button>
                    <span> or </span>
                    <Button component={Link} to={`${location.pathname}/subscription-group/add-new`}>
                      Create new
                    </Button>
                  </Grid>
                </PolicyAuthorized>
              </>
            )}
          </PolicyAuthorized>
        </Grid>
      </DetailsPageLayout>
    );
  }

  return <Alert severity="error">Unsupported subscription platform type</Alert>;
}
